import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

/**
 * Last-known API data per key, kept for the session, so a screen staff return
 * to paints at once and reloads behind it (stale-while-revalidate). Every
 * mount still asks the server — the cache removes the blank wait, it never
 * stands in for the server. Cleared on sign-in and sign-out (AuthContext), so
 * one account never sees another's data.
 *
 * The key must name every input the fetcher reads (shop, filters, page): two
 * screens that pass one key share one entry, which is the point — the
 * appointments list and the calendar load the same window.
 */
const cache = new Map<string, unknown>();
/** The newest request per key; an older one that finishes late is not cached. */
const latest = new Map<string, Promise<unknown>>();
/**
 * The JSON of what each entry was loaded as. A reload that returns the same
 * data keeps the old objects, so the screen does not re-render all of it.
 */
const loadedAs = new Map<string, string>();
const listeners = new Map<string, Set<() => void>>();

export function clearCachedData() {
  cache.clear();
  latest.clear();
  loadedAs.clear();
  listeners.forEach((set) => set.forEach((listener) => listener()));
}

function notify(key: string) {
  listeners.get(key)?.forEach((listener) => listener());
}

function fetchInto<T>(key: string, fetcher: () => Promise<T>, shareInFlight: boolean): Promise<T> {
  const pending = latest.get(key);
  if (shareInFlight && pending) return pending as Promise<T>;

  const request = fetcher().then((data) => {
    if (latest.get(key) === request) {
      const json = JSON.stringify(data);
      if (!cache.has(key) || loadedAs.get(key) !== json) {
        cache.set(key, data);
        loadedAs.set(key, json);
        notify(key);
      }
    }
    return data;
  });
  const settle = () => {
    if (latest.get(key) === request) latest.delete(key);
  };
  request.then(settle, settle);
  latest.set(key, request);
  return request;
}

export interface CachedData<T> {
  /** The last data loaded for this key — from an earlier visit until this one's load lands. */
  data: T | undefined;
  /** True only while there is nothing to show yet; a background reload is silent. */
  loading: boolean;
  error: unknown;
  /** Fetches again (never sharing a request already in flight) and resolves with the result. */
  reload: () => Promise<T | undefined>;
  /**
   * Local edit, like a state setter, that wins over a load already in flight.
   * The updater form does nothing until data has loaded (that load brings the
   * change anyway), and returning `previous` unchanged is a no-op.
   */
  mutate: (next: T | ((previous: T) => T)) => void;
}

/**
 * Loads `fetcher` under `key` (null skips loading, e.g. before the shop is
 * known). Errors are returned, never thrown; read `error` to show them.
 */
export function useCachedData<T>(key: string | null, fetcher: () => Promise<T>): CachedData<T> {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const [failure, setFailure] = useState<{ key: string; error: unknown } | null>(null);

  useEffect(() => {
    if (!key) return;
    let set = listeners.get(key);
    if (!set) listeners.set(key, (set = new Set()));
    set.add(rerender);
    return () => {
      set.delete(rerender);
      if (set.size === 0) listeners.delete(key);
    };
  }, [key]);

  useEffect(() => {
    if (!key) return;
    let active = true;
    fetchInto(key, () => fetcherRef.current(), true).then(
      () => active && setFailure(null),
      (error) => active && setFailure({ key, error }),
    );
    return () => {
      active = false;
    };
  }, [key]);

  const reload = useCallback(async () => {
    if (!key) return undefined;
    try {
      const data = await fetchInto(key, () => fetcherRef.current(), false);
      setFailure(null);
      return data;
    } catch (error) {
      setFailure({ key, error });
      return undefined;
    }
  }, [key]);

  const mutate = useCallback(
    (next: T | ((previous: T) => T)) => {
      if (!key) return;
      let value = next as T;
      if (typeof next === 'function') {
        if (!cache.has(key)) return;
        value = (next as (previous: T) => T)(cache.get(key) as T);
      }
      if (cache.has(key) && Object.is(cache.get(key), value)) return;
      // A load that started before this edit would overwrite it with older data.
      latest.delete(key);
      loadedAs.delete(key);
      cache.set(key, value);
      notify(key);
    },
    [key],
  );

  const error = key && failure?.key === key ? failure.error : null;
  const has = key !== null && cache.has(key);
  return {
    data: has ? (cache.get(key) as T) : undefined,
    loading: key !== null && !has && !error,
    error,
    reload,
    mutate,
  };
}
